"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Building2, Star, Code as Code2, FileText, Search, Link2, ChartBar as BarChart3, MousePointerClick, Layers, CircleCheck as CheckCircle2, Bot, ArrowRight, PhoneCall } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
const services = [{
  id: "gbp-optimization",
  title: "Google Business Profile Optimization",
  description: "Full audit, setup, and strategic optimization of your Google Business Profile to claim top placement in local 3-pack search results.",
  iconName: "MapPin",
  features: ["Primary category & attribute tuning", "Geo-tagged photo strategy", "Weekly profile management"],
  link: "/LocalSEOPage"
}, {
  id: "local-citations",
  title: "Local Citation Building & Cleanup",
  description: "Consistent Name, Address, and Phone (NAP) listing distribution across authoritative UK and global business directories.",
  iconName: "Building2",
  features: ["Duplicate listing suppression", "High-DA local directory submissions", "NAP consistency verification"],
  link: "/LocalSEOPage"
}, {
  id: "review-management",
  title: "Review Strategy & Reputation Guidance",
  description: "Ethical customer feedback frameworks that systematically increase genuine 5-star Google reviews and convert prospects.",
  iconName: "Star",
  features: ["Automated response strategy", "Review acquisition workflows", "Sentiment analysis"],
  link: "/LocalSEOPage"
}, {
  id: "local-schema",
  title: "Local Schema & Structured Data",
  description: "Custom JSON-LD schema implementation helping search engine crawlers understand your exact service areas and physical location.",
  iconName: "Code2",
  features: ["LocalBusiness & GeoCoordinates markup", "OpeningHours & Service Schema", "Validation via Google Rich Results"],
  link: "/TechnicalSeoPage"
}, {
  id: "geo-targeted-content",
  title: "Geo-Targeted Content Development",
  description: "Hyper-local landing pages and service area copy crafted to attract intent-driven local customers without spammy door pages.",
  iconName: "FileText",
  features: ["City & neighborhood targeting", "Customer intent matching", "E-E-A-T local proof signals"],
  link: "/LocalSEOPage"
}, {
  id: "local-keyword-research",
  title: "Local Keyword & Intent Analysis",
  description: "Uncovering high-converting local search phrases, near-me search queries, and transactional terms in your target markets.",
  iconName: "Search",
  features: ["Search volume & difficulty metrics", "Near-me query mapping", "Competitor keyword gap analysis"],
  link: "/LocalSEOPage"
}, {
  id: "hyper-local-linkbuilding",
  title: "Hyper-Local Backlink Acquisition",
  description: "Earning contextual local backlinks from regional news outlets, local business chambers, sponsorships, and community partners.",
  iconName: "Link2",
  features: ["Community outreach campaigns", "Local sponsorship link building", "PR & local news placements"],
  link: "/LocalSEOPage"
}, {
  id: "competitor-analysis",
  title: "Local Competitor & SERP Audits",
  description: "In-depth research into top-ranking local competitors to uncover exact link, content, and profile advantages to surpass them.",
  iconName: "BarChart3",
  features: ["3-pack ranking signal analysis", "Competitor link profile audit", "Actionable gap report"],
  link: "/SeoAuditsPage"
}, {
  id: "local-cro",
  title: "Local Conversion Rate Optimization",
  description: "Optimizing your website UX and call-to-actions so that inbound local visitors easily convert into phone calls and lead submissions.",
  iconName: "MousePointerClick",
  features: ["Click-to-call mobile setup", "Trust badge positioning", "Frictionless contact form design"],
  link: "/LocalSEOPage"
}, {
  id: "multi-location-seo",
  title: "Multi-Location Local SEO",
  description: "Scalable local strategy for regional enterprises, multi-branch service providers, and multi-city franchises.",
  iconName: "Layers",
  features: ["Branch page architecture", "Centralized profile management", "Location-specific reporting"],
  link: "/LocalSEOPage"
}, {
  id: "technical-local-audits",
  title: "Local Technical SEO Audits",
  description: "Comprehensive health checks ensuring mobile site speed, crawlability, indexing, and seamless map integrations.",
  iconName: "CheckCircle2",
  features: ["Mobile-first indexing checks", "Core Web Vitals tuning", "Google Maps embed optimization"],
  link: "/TechnicalSeoPage"
}, {
  id: "ai-geo-search",
  title: "AI Search & Voice Optimization",
  description: "Preparing your local business for modern AI search engines, voice assistants, and Google AI Overviews for geo queries.",
  iconName: "Bot",
  features: ["Conversational local queries", "Apple Maps & AI readiness", "Entity relationship building"],
  link: "/AISearchOptimisationPage"
}];
export default function LocalServices() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case "MapPin":
        return <MapPin className="size-5" />;
      case "Building2":
        return <Building2 className="size-5" />;
      case "Star":
        return <Star className="size-5" />;
      case "Code2":
        return <Code2 className="size-5" />;
      case "FileText":
        return <FileText className="size-5" />;
      case "Search":
        return <Search className="size-5" />;
      case "Link2":
        return <Link2 className="size-5" />;
      case "BarChart3":
        return <BarChart3 className="size-5" />;
      case "MousePointerClick":
        return <MousePointerClick className="size-5" />;
      case "Layers":
        return <Layers className="size-5" />;
      case "CheckCircle2":
        return <CheckCircle2 className="size-5" />;
      case "Bot":
        return <Bot className="size-5" />;
      default:
        return <MapPin className="size-5" />;
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="localseoservicesgrid" data-nav="light" className="w-full bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4 px-3 py-1 text-xs md:text-sm font-medium border-primary/20 text-primary bg-primary/5 rounded-full">
            Local SEO Expertise
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-serif">
            Comprehensive Local SEO Solutions Built for Growth
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            I work directly with you to craft bespoke local search strategies. Here are the 12 core areas I optimize to secure top rankings in your target geographical locations.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, i) => <motion.div key={service.id} variants={itemVariants} data-index={i} className="h-full">
              <Card className="h-full flex flex-col border border-border/80 hover:border-primary/40 shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300 bg-card rounded-xl overflow-hidden group">
                <CardHeader className="bg-primary text-primary-foreground p-5 flex-row items-center gap-3 space-y-0">
                  <div className="p-2.5 rounded-lg bg-primary-foreground/10 text-primary-foreground shrink-0">
                    {getIcon(service.iconName)}
                  </div>
                  <CardTitle className="text-lg font-semibold font-serif leading-snug line-clamp-2">
                    {service.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="pt-6 pb-4 flex-grow space-y-4">
                  <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                    {service.description}
                  </CardDescription>

                  <div className="space-y-2 pt-2 border-t border-border/60">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Key Deliverables:
                    </span>
                    <ul className="space-y-1.5">
                      {service.features.map((feature, idx) => <li key={idx} className="text-xs text-foreground/90 flex items-start gap-2">
                          <CheckCircle2 className="size-3.5 text-primary shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>)}
                    </ul>
                  </div>
                </CardContent>

                <CardFooter className="pt-0 pb-5 px-6 mt-auto">
                  <Button asChild variant="outline" size="sm" className="w-full justify-between text-xs font-medium border-border hover:bg-primary/5 hover:text-primary group-hover:border-primary/30">
                    <Link to={service.link}>
                      <span>Explore Service</span>
                      <ArrowRight className="size-3.5 ml-1 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        <div className="mt-16 bg-muted/50 border border-border rounded-2xl p-6 sm:p-8 lg:p-10 flex flex-col md:flex-row items-center justify-between gap-6 shadow-xs">
          <div className="space-y-2 text-center md:text-left">
            <h3 className="text-xl sm:text-2xl font-semibold font-serif text-foreground">
              Need a Custom Local SEO Strategy?
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground max-w-2xl font-sans">
              You will work directly with me—Yassine Essaadi—not an account manager or junior assistant. Let’s discuss your current local rankings and growth potential.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0 w-full md:w-auto">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
              <Link to="/ContactPage">
                <PhoneCall className="size-4 mr-2" />
                Book Free Consultation
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-border">
              <Link to="/CaseStudiesPage">
                View Case Studies
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}